/**
 * Import de l'export texte **Kaspersky Password Manager** (« Parametres >
 * Importer / Exporter > Exporter en fichier texte »).
 *
 * Purement local, aucune E/S reseau : ce module transforme l'export en
 * `ImportedEntry`. Le fichier est decoupe en sections (`Websites`,
 * `Applications`, `Notes`), chacune en fiches separees par une ligne `---`,
 * chaque fiche en lignes `Cle: valeur`. `Comment` (identifiants) et `Text`
 * (notes) courent jusqu'a la fin de la fiche : leurs lignes suivantes leur
 * appartiennent, deux-points compris. Toute cle inconnue devient un champ
 * personnalise, masque si son libelle designe un secret (aucune perte).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, type ImportedEntry } from './csv-import.js';

type KasperskySection = 'website' | 'application' | 'note';

/** En-tetes de section de l'export, chacun suivi d'une ligne vide. */
const KASPERSKY_SECTIONS: Readonly<Record<string, KasperskySection>> = {
  Websites: 'website',
  Applications: 'application',
  Notes: 'note',
};

/** Premiere cle d'une fiche de chaque section : ce qui suit un `---` ouvre une fiche si et seulement s'il commence par elle. */
const KASPERSKY_FIRST_KEY: Readonly<Record<KasperskySection, string>> = {
  website: 'Website name:',
  application: 'Application:',
  note: 'Name:',
};

/** Cles dont la valeur court sur toutes les lignes suivantes de la fiche. */
const KASPERSKY_MULTILINE = new Set(['Comment', 'Text']);

/** Cles reprises dans l'entree elle-meme (les autres deviennent des champs). */
const KASPERSKY_CONSUMED: Readonly<Record<KasperskySection, ReadonlySet<string>>> = {
  website: new Set(['Website name', 'Website URL', 'Login name', 'Login', 'Password', 'Comment']),
  application: new Set(['Application', 'Login name', 'Login', 'Password', 'Comment']),
  note: new Set(['Name', 'Text']),
};

const KASPERSKY_DELIMITER = '---';

function kasperskyLines(text: string): string[] {
  return text.replace(/^﻿/u, '').replace(/\r\n?/gu, '\n').split('\n');
}

function sectionHeaderAt(lines: readonly string[], index: number): KasperskySection | undefined {
  const section = KASPERSKY_SECTIONS[lines[index]!.trim()];
  return section !== undefined && (lines[index + 1] ?? '').trim() === '' ? section : undefined;
}

/**
 * Le texte est-il un export Kaspersky ? Premiere ligne non vide = en-tete de
 * section suivi d'une ligne vide, puis la premiere cle d'une fiche de cette
 * section. Un CSV ou un JSON n'y ressemble jamais.
 */
export function isKasperskyTxt(text: string): boolean {
  const lines = kasperskyLines(text);
  const first = lines.findIndex((line) => line.trim() !== '');
  if (first === -1) return false;
  const section = sectionHeaderAt(lines, first);
  if (section === undefined) return false;
  const next = lines.slice(first + 1).find((line) => line.trim() !== '');
  return next !== undefined && next.startsWith(KASPERSKY_FIRST_KEY[section]);
}

/**
 * Une ligne `---` ferme la fiche seulement si ce qui suit ouvre une fiche,
 * une section ou termine le fichier : un `---` ecrit dans un commentaire
 * reste dans le commentaire.
 */
function closesRecord(lines: readonly string[], index: number, section: KasperskySection): boolean {
  for (let next = index + 1; next < lines.length; next += 1) {
    if (lines[next]!.trim() === '') continue;
    return sectionHeaderAt(lines, next) !== undefined || lines[next]!.startsWith(KASPERSKY_FIRST_KEY[section]);
  }
  return true;
}

/**
 * Lignes d'une fiche -> paires `[cle, valeur]` dans l'ordre. Coupe au PREMIER
 * `:` (une valeur garde ses deux-points) ; une seule espace de tete est retiree,
 * jamais celles d'un mot de passe. Une ligne sans `:` hors commentaire est
 * gardee sous une cle vide, jamais perdue.
 */
export function parseKasperskyRecord(lines: readonly string[]): [string, string][] {
  const pairs: [string, string][] = [];
  let multiline: { key: string; lines: string[] } | null = null;
  for (const line of lines) {
    if (multiline !== null) {
      multiline.lines.push(line);
      continue;
    }
    if (line.trim() === '') continue;
    const colon = line.indexOf(':');
    if (colon === -1) {
      pairs.push(['', line.trim()]);
      continue;
    }
    const key = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).replace(/^ /u, '');
    if (KASPERSKY_MULTILINE.has(key)) multiline = { key, lines: [value] };
    else pairs.push([key, value]);
  }
  if (multiline !== null) pairs.push([multiline.key, multiline.lines.join('\n').replace(/\s+$/u, '')]);
  return pairs;
}

function kasperskyEntry(section: KasperskySection, pairs: readonly [string, string][]): ImportedEntry | null {
  if (pairs.length === 0) return null;
  const values = new Map<string, string>();
  const customFields: EntryCustomField[] = [];
  for (const [key, value] of pairs) {
    // Premiere occurrence d'une cle reprise = la valeur de l'entree ; un doublon reste un champ.
    if (KASPERSKY_CONSUMED[section].has(key) && !values.has(key)) values.set(key, value);
    else if (value.trim() !== '') customFields.push(csvColumnField(key || 'Champ', value));
  }
  const get = (key: string): string => values.get(key) ?? '';
  const extra = customFields.length > 0 ? { customFields } : {};
  if (section === 'note') {
    return { type: 'note', name: get('Name').trim() || 'Sans nom', username: '', password: '', uris: [], notes: get('Text'), totpSecret: '', favorite: false, ...extra };
  }
  const site = get(section === 'website' ? 'Website name' : 'Application').trim();
  // `Login name` = nom du compte que Kaspersky affiche pour distinguer plusieurs comptes d'un meme site.
  const accountName = get('Login name').trim();
  const url = get('Website URL').trim();
  const username = get('Login').trim();
  const name = site && accountName && accountName !== site ? `${site} (${accountName})` : site || accountName || url || username || 'Sans nom';
  return {
    type: 'login',
    name,
    username,
    password: get('Password'),
    uris: url.length > 0 ? [url] : [],
    notes: get('Comment'),
    totpSecret: '',
    favorite: false,
    ...extra,
  };
}

/**
 * Parse un export texte Kaspersky Password Manager.
 *
 * @throws {Error} le texte n'est pas un export Kaspersky (`isKasperskyTxt`)
 */
export function parseKasperskyTxt(text: string): ImportedEntry[] {
  if (!isKasperskyTxt(text)) throw new Error('export Kaspersky Password Manager invalide');
  const lines = kasperskyLines(text);
  const entries: ImportedEntry[] = [];
  let section: KasperskySection | null = null;
  let record: string[] = [];
  const flush = (): void => {
    if (section !== null) {
      const entry = kasperskyEntry(section, parseKasperskyRecord(record));
      if (entry !== null) entries.push(entry);
    }
    record = [];
  };
  lines.forEach((line, index) => {
    const header = sectionHeaderAt(lines, index);
    if (header !== undefined && record.every((pending) => pending.trim() === '')) {
      flush();
      section = header;
      return;
    }
    if (line.trim() === KASPERSKY_DELIMITER && section !== null && closesRecord(lines, index, section)) {
      flush();
      return;
    }
    record.push(line);
  });
  flush();
  return entries;
}
